import type {
	OptimizationModel,
	PricingCampaignReassignRequest,
} from '@/shared/generated'

import { Fragment, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronLeftIcon, ChevronRightIcon, InfoIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { SelectOptimizationGroup } from '@/features/select-optimization-groups'

import { resetOptimizations } from '@/entities/optimization'
import { useReassignPricingCampaignsMutation } from '@/entities/pricing-campaign'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalDescription,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import useStep from '@/shared/hooks/useStep'

import Checkout from './CheckoutView'
import ExecutionView from './ExecutionView'

type Props = {
	isOpen: boolean
	close(): void
	selected: string[]
	resetSelectedItems?(): void
}
export default function MovePricingCampaigns({
	isOpen,
	close,
	selected: selectedCampaigns,
	resetSelectedItems,
}: Props) {
	const { t } = useTranslation()

	const [selectedGroup, setSelectedGroup] = useState<string | null>(null)
	const [currentStep, helpers] = useStep({ maxStep: 3 })
	const queryClient = useQueryClient()
	const reassign = useReassignPricingCampaignsMutation({
		mutationKey: ['reassignPricingCampaigns'],
		async onMutate(variables: PricingCampaignReassignRequest) {
			const optimizations = queryClient.getQueryData<OptimizationModel[]>([
				'optimizations',
			])
			await resetOptimizations(optimizations || [], [
				variables.dst_optimization_group_id,
			])
		},
		onSuccess() {
			resetSelectedItems?.()
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns-segments'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns-overview'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricings-settings'],
			})
			queryClient.invalidateQueries({
				queryKey: ['products-counts-by-pc'],
			})
		},
	})
	const { goToNextStep, goToPrevStep } = helpers
	const formData = {
		campaigns: selectedCampaigns,
		groups: selectedGroup ? [selectedGroup] : [],
	}
	const handleExecute = () => {
		const requestBody: PricingCampaignReassignRequest = {
			src_pricing_campaign_ids: formData.campaigns,
			dst_optimization_group_id: formData.groups[0],
		}
		reassign.mutateAsync(requestBody)
	}

	return (
		<Modal open={isOpen} onOpenChange={close}>
			<ModalContent className='max-w-5xl'>
				{currentStep === 1 && (
					<Fragment>
						<ModalHeader>
							<ModalTitle>{t('pc.modal.reassign_1.title')}</ModalTitle>
							<ModalDescription>
								{t('pc.modal.reassign_1.subtitle')}
							</ModalDescription>
						</ModalHeader>
						<ErrorBoundary>
							<ModalBody className='space-y-3'>
								<SelectOptimizationGroup
									selectedGroup={selectedGroup}
									setSelectedGroup={setSelectedGroup}
								/>
								<Alert variant='info'>
									<InfoIcon />
									<AlertDescription>
										{t('note.copy_pc_general_note')}
									</AlertDescription>
								</Alert>
							</ModalBody>
						</ErrorBoundary>
						<ModalFooter>
							<ModalClose asChild>
								<Button className='mr-auto'>{t('general_cancel')}</Button>
							</ModalClose>
							<Button
								variant='primary'
								disabled={!selectedGroup}
								onClick={goToNextStep}
							>
								{t('general_next')}
								<ChevronRightIcon />
							</Button>
						</ModalFooter>
					</Fragment>
				)}
				{currentStep === 2 && (
					<Fragment>
						<ModalHeader>
							<ModalTitle>{t('pc.modal.reassign_2.title')}</ModalTitle>
							<ModalDescription>
								{t('pc.modal.reassign_2.subtitle')}
							</ModalDescription>
						</ModalHeader>
						<ErrorBoundary>
							<ModalBody className='space-y-3'>
								<Checkout formData={formData} />
								<Alert variant='info'>
									<InfoIcon />
									<AlertDescription>
										{t('note.copy_pc_general_note')}
									</AlertDescription>
								</Alert>
							</ModalBody>
						</ErrorBoundary>
						<ModalFooter>
							<ModalClose asChild>
								<Button className='mr-auto'>{t('general_cancel')}</Button>
							</ModalClose>
							<Button variant='secondary' onClick={goToPrevStep}>
								<ChevronLeftIcon />
								{t('general_back')}
							</Button>
							<Button
								variant='primary'
								onClick={() => {
									handleExecute()
									goToNextStep()
								}}
							>
								{t('app.reassign')}
								<ChevronRightIcon />
							</Button>
						</ModalFooter>
					</Fragment>
				)}
				{currentStep === 3 && (
					<Fragment>
						<ModalHeader>
							<ModalTitle>{t('pc.modal.reassign_3.title')}</ModalTitle>
							<ModalDescription>
								{t('pc.modal.reassign_3.subtitle')}
							</ModalDescription>
						</ModalHeader>
						<ErrorBoundary>
							<ModalBody className='min-h-100'>
								<ExecutionView formData={formData} run={reassign} />
							</ModalBody>
						</ErrorBoundary>
						<ModalFooter>
							<Button
								disabled={reassign.isPending}
								variant='secondary'
								onClick={goToPrevStep}
							>
								<ChevronLeftIcon />
								{t('general_back')}
							</Button>
							<ModalClose asChild>
								<Button disabled={reassign.isPending} variant='primary'>
									{t('general_close_and_finish')}
								</Button>
							</ModalClose>
						</ModalFooter>
					</Fragment>
				)}
			</ModalContent>
		</Modal>
	)
}
